import React, { useState, useRef, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Alert,
  CircularProgress,
  IconButton,
  Paper
} from '@mui/material';
import {
  CameraAlt,
  PhotoCamera,
  Refresh,
  Check,
  Close,
  Upload
} from '@mui/icons-material';

function BusPhotoCapture({ open, onClose, onPhotoTaken, busNumber, allowCloseWithoutPhoto = false, onCloseWithoutPhoto }) {
  const [capturedImage, setCapturedImage] = useState(null);
  const [isCapturing, setIsCapturing] = useState(false);
  const [error, setError] = useState(null);
  const [cameraStream, setCameraStream] = useState(null);
  const [showCamera, setShowCamera] = useState(false);
  
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const fileInputRef = useRef(null);

  // Start camera
  const startCamera = async () => {
    try {
      setError(null);
      setIsCapturing(true);
      console.log('🎥 Starting camera...');
      
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { 
          width: { ideal: 1280 }, 
          height: { ideal: 720 },
          facingMode: 'user' // Use front camera (laptop camera)
        }
      });
      
      console.log('✅ Camera stream obtained:', stream);
      setCameraStream(stream);
      setShowCamera(true);
      setIsCapturing(false);
      
      // Wait for the video element to be rendered before setting srcObject
      setTimeout(() => {
        if (videoRef.current) {
          console.log('📹 Setting video srcObject...');
          console.log('📹 Video element:', videoRef.current);
          console.log('📹 Stream:', stream);
          videoRef.current.srcObject = stream;
          console.log('📹 srcObject set successfully');
          
          // Wait for video to load
          videoRef.current.onloadedmetadata = () => {
            console.log('📺 Video metadata loaded');
            console.log('📺 Video dimensions:', videoRef.current.videoWidth, 'x', videoRef.current.videoHeight);
            videoRef.current.play().then(() => {
              console.log('▶️ Video started playing');
              console.log('▶️ Video currentTime:', videoRef.current.currentTime);
            }).catch(err => {
              console.error('❌ Error playing video:', err);
            });
          };
          
          // Add error handler
          videoRef.current.onerror = (err) => {
            console.error('❌ Video error:', err);
          };
        } else {
          console.error('❌ Video ref is still null after timeout!');
        }
      }, 100);
    } catch (err) {
      console.error('❌ Error accessing camera:', err);
      setError('שגיאה בגישה למצלמה. אנא ודא שנתת הרשאות למצלמה.');
      setIsCapturing(false);
    }
  };

  // Stop camera
  const stopCamera = () => {
    if (cameraStream) {
      cameraStream.getTracks().forEach(track => track.stop());
      setCameraStream(null);
    }
    setShowCamera(false);
  };

  // Capture photo from camera
  const capturePhoto = () => {
    if (!videoRef.current || !canvasRef.current) return;

    const video = videoRef.current;
    const canvas = canvasRef.current;
    const context = canvas.getContext('2d');

    // Set canvas dimensions to match video
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;

    // Draw video frame to canvas
    context.drawImage(video, 0, 0, canvas.width, canvas.height);

    // Convert to base64
    const imageBase64 = canvas.toDataURL('image/jpeg', 0.8);
    setCapturedImage(imageBase64);
    stopCamera();
  };

  // Handle file upload
  const handleFileUpload = useCallback((event) => {
    const file = event.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      setCapturedImage(e.target.result);
    };
    reader.readAsDataURL(file);
  }, []);

  // Confirm and send photo
  const confirmPhoto = () => {
    if (capturedImage && onPhotoTaken) {
      onPhotoTaken(capturedImage);
    }
  };

  // Retake photo
  const retakePhoto = () => {
    setCapturedImage(null);
    setError(null);
  };

  // Handle dialog close
  const handleClose = () => {
    stopCamera();
    setCapturedImage(null);
    setError(null);
    setShowCamera(false);
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        style: { minHeight: '500px' }
      }}
    >
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          <PhotoCamera color="primary" />
          <Typography variant="h6">
            צילום תמונה לסגירת אוטובוס {busNumber}
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent>
        <Box display="flex" flexDirection="column" alignItems="center" gap={2}>
          {error && (
            <Alert severity="error" sx={{ width: '100%' }}>
              {error}
            </Alert>
          )}

          {!showCamera && !capturedImage && (
            <Box display="flex" flexDirection="column" alignItems="center" gap={3}>
              <Typography variant="body1" textAlign="center" color="text.secondary">
                אנא צלם תמונה של האוטובוס לפני סגירתו
              </Typography>
              
              <Box display="flex" gap={2}>
                <Button
                  variant="contained"
                  startIcon={<CameraAlt />}
                  onClick={startCamera}
                  disabled={isCapturing}
                  size="large"
                >
                  {isCapturing ? <CircularProgress size={20} /> : 'פתח מצלמה'}
                </Button>
                
                <Button
                  variant="outlined"
                  startIcon={<Upload />}
                  onClick={() => fileInputRef.current?.click()}
                  size="large"
                >
                  העלה תמונה
                </Button>
              </Box>
            </Box>
          )}

          {showCamera && (
            <Paper elevation={3} sx={{ p: 2, maxWidth: '100%' }}>
              <Box display="flex" flexDirection="column" alignItems="center" gap={2}>
                <video
                  ref={videoRef}
                  autoPlay
                  playsInline
                  muted
                  style={{
                    width: '100%',
                    maxWidth: '400px',
                    height: 'auto',
                    borderRadius: '8px',
                    backgroundColor: '#f0f0f0',
                    border: '2px solid #ddd'
                  }}
                />
                
                <Box display="flex" gap={2}>
                  <Button
                    variant="contained"
                    color="primary"
                    startIcon={<PhotoCamera />}
                    onClick={capturePhoto}
                    size="large"
                  >
                    צלם
                  </Button>
                  
                  <Button
                    variant="outlined"
                    onClick={stopCamera}
                    size="large"
                  >
                    ביטול
                  </Button>
                </Box>
              </Box>
            </Paper>
          )}

          {capturedImage && (
            <Paper elevation={3} sx={{ p: 2, maxWidth: '100%' }}>
              <Box display="flex" flexDirection="column" alignItems="center" gap={2}>
                <Typography variant="h6">תצוגה מקדימה</Typography>
                <img
                  src={capturedImage}
                  alt="Captured bus"
                  style={{
                    width: '100%',
                    maxWidth: '400px',
                    height: 'auto',
                    borderRadius: '8px'
                  }}
                />
                
                <Box display="flex" gap={2}>
                  <Button
                    variant="contained"
                    color="success"
                    startIcon={<Check />}
                    onClick={confirmPhoto}
                    size="large"
                  >
                    אישור ושליחה
                  </Button>
                  
                  <Button
                    variant="outlined"
                    startIcon={<Refresh />}
                    onClick={retakePhoto}
                    size="large"
                  >
                    צלם שוב
                  </Button>
                </Box>
              </Box>
            </Paper>
          )}

          {/* Hidden file input */}
          <input
            type="file"
            ref={fileInputRef}
            accept="image/*"
            capture="environment"
            onChange={handleFileUpload}
            style={{ display: 'none' }}
          />

          {/* Hidden canvas for photo capture */}
          <canvas ref={canvasRef} style={{ display: 'none' }} />
        </Box>
      </DialogContent>

      <DialogActions>
        {allowCloseWithoutPhoto && (
          <Button
            onClick={onCloseWithoutPhoto}
            color="warning"
            variant="outlined"
          >
            סגור ללא תמונה
          </Button>
        )}
        <Button
          onClick={handleClose}
          startIcon={<Close />}
          color="inherit"
        >
          ביטול
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default BusPhotoCapture;
